/**
 * SocialPulseCallback.jsx — OAuth 2.0 Authorization Code + PKCE callback.
 *
 * Route: /auth/socialpulse-callback
 *
 * Flow:
 *   1. SocialPulse redirects here with ?code=&state=
 *   2. We verify state matches what we stashed in sessionStorage.
 *   3. Exchange the code with the SocialPulse token endpoint
 *      (public client — PKCE only, no secret) to get an id_token.
 *   4. POST /api/auth/oidc-session { id_token } to mint an IFT session.
 *   5. Store JWT the same way AuthContext expects and navigate to
 *      the original `next=` target.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

const ISSUER = process.env.REACT_APP_SOCIALPULSE_OIDC_ISSUER || "";
const CLIENT_ID = process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID || "";

export default function SocialPulseCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("working"); // working | error
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const err = params.get("error");
      const expectedState = sessionStorage.getItem("sp_oidc_state");
      const verifier = sessionStorage.getItem("sp_oidc_verifier");
      const next = sessionStorage.getItem("sp_oidc_next") || "/";
      sessionStorage.removeItem("sp_oidc_state");
      sessionStorage.removeItem("sp_oidc_verifier");
      sessionStorage.removeItem("sp_oidc_next");

      if (err) { setError(`SocialPulse returned: ${err}`); return setStatus("error"); }
      if (!code || !state || !verifier) { setError("Missing code / state / verifier. Please retry."); return setStatus("error"); }
      if (state !== expectedState) { setError("State mismatch. Possible CSRF — retry."); return setStatus("error"); }
      if (!ISSUER || !CLIENT_ID) { setError("SocialPulse SSO is not configured for this build."); return setStatus("error"); }

      const redirectUri = `${window.location.origin}/auth/socialpulse-callback`;

      // 1) Token exchange (PKCE — public client, no secret).
      let idToken = null;
      try {
        const form = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: CLIENT_ID,
          code,
          code_verifier: verifier,
          redirect_uri: redirectUri,
        });
        const res = await fetch(`${ISSUER.replace(/\/$/, "")}/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: form.toString(),
        });
        if (!res.ok) throw new Error(`Token exchange failed: HTTP ${res.status}`);
        const data = await res.json();
        idToken = data.id_token;
        if (!idToken) throw new Error("SocialPulse did not return an id_token.");
      } catch (e) {
        setError(String(e?.message || e));
        return setStatus("error");
      }

      // 2) Hand the id_token to our backend for verification + session.
      try {
        const { data } = await http.post("/auth/oidc-session", { id_token: idToken });
        if (data?.token) {
          // Mirror what AuthContext does elsewhere in the app.
          localStorage.setItem("socialpulse_jwt", data.token);
          toast.success(data.is_new_user ? "Welcome to IndianFamilyTree!" : "Signed in via SocialPulse.");
          navigate(next, { replace: true });
          // Give AuthContext a chance to notice via a hard reload — safe
          // fallback if it doesn't listen to storage events.
          setTimeout(() => { window.location.reload(); }, 150);
          return;
        }
        throw new Error("Backend did not return a session token.");
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Session exchange failed.");
        return setStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "working") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="socialpulse-callback-loading">
        <div className="text-center space-y-2">
          <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-70" />
          <p className="text-sm opacity-70">Finishing SocialPulse sign-in…</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="socialpulse-callback-error">
      <div className="max-w-md text-center space-y-3">
        <ShieldAlert className="w-6 h-6 mx-auto" />
        <h1 className="font-display text-xl">Could not finish SocialPulse sign-in</h1>
        <p className="text-sm opacity-70">{error}</p>
        <button onClick={() => navigate("/login")} className="wofy-btn-primary text-sm">Try another sign-in method</button>
      </div>
    </div>
  );
}
